// Austrian Payroll Engine - implements the abstract PayrollEngine for Austrian compliance

import { supabase } from '@/integrations/supabase/client';
import { Employee } from '@/types';
import { PayrollEngine } from '../core/PayrollEngine';
import {
  CountryPayrollConstants,
  PayrollInputData,
  GrossPayComponents,
  InsuranceCalculation,
  TaxCalculation,
  DeductionCalculation,
  ValidationError,
  CalculationError
} from '../core/types';

// Austrian-specific types
export type AustrianContractType = 'VOLLZEIT' | 'TEILZEIT' | 'GERINGFÜGIG' | 'BEFRISTET';

interface AustrianPayrollConstants {
  year: number;
  country_code: string;
  
  // Minimum wage and social standards
  minimum_wage_hourly: number;
  minimum_wage_monthly: number;
  
  // Tax system (Austrian income tax brackets for 2024)
  basic_tax_allowance: number; // €11,000 (Grundfreibetrag)
  tax_rate_20_percent_limit: number; // Up to €18,000
  tax_rate_32_5_percent_limit: number; // €18,000 - €31,000
  tax_rate_42_percent_limit: number; // €31,000 - €60,000
  tax_rate_48_percent_limit: number; // €60,000 - €90,000
  tax_rate_50_percent_limit: number; // €90,000 - €1,000,000
  tax_rate_55_percent: number; // Above €1,000,000
  
  // Social insurance rates (2024)
  pension_rate_employee: number; // 10.25%
  pension_rate_employer: number; // 12.55%
  unemployment_rate_employee: number; // 3%
  unemployment_rate_employer: number; // 3%
  accident_rate_employer: number; // 1.3% (employer only)
  
  // Health insurance
  health_rate_employee: number; // 3.87%
  health_rate_employer: number; // 3.78%
  
  // Family burden equalization fund
  family_fund_rate_employer: number; // 4.5%
  
  // Assessment limits
  pension_assessment_limit_monthly: number; // €6,060
  health_assessment_limit_monthly: number; // €5,550
  
  // Geringfügigkeit threshold (marginal employment)
  geringfuegig_threshold: number; // €500.91 per month
  
  // 13th and 14th salary special rates
  special_payment_tax_limit: number; // €620 tax-free
  
  // Holiday and overtime premiums
  overtime_premium_rate: number; // 50% for hours beyond 40/week
  night_work_premium_rate: number; // 25%
  sunday_premium_rate: number; // 100%
  holiday_premium_rate: number; // 100%
  
  // Chamber membership (Kammerumlage)
  chamber_fee_rate: number; // 0.5%
  chamber_fee_limit: number; // Maximum fee
}

/**
 * Austrian Payroll Calculation Engine
 * Implements Austrian-specific payroll calculations including taxes,
 * social insurance, and employment classifications
 */
export class AustrianPayrollEngine extends PayrollEngine {
  private employee!: Employee;
  private austrianConstants!: AustrianPayrollConstants;
  private year!: number;

  constructor(countryCode: string = 'AT', debugMode: boolean = false) {
    super(countryCode, debugMode);
  }

  /**
   * Load Austrian payroll constants from database
   */
  protected async loadPayrollConstants(year: number): Promise<CountryPayrollConstants> {
    this.log(`Loading Austrian payroll constants for year ${year}`);
    
    const { data, error } = await supabase
      .from('payroll_constants')
      .select('*')
      .eq('year', year)
      .eq('country_code', 'AT')
      .single();

    if (error || !data) {
      this.austrianConstants = this.createDefaultAustrianConstants(year);
      this.log('Using default Austrian constants (database constants not found)');
    } else {
      this.austrianConstants = data as AustrianPayrollConstants;
    }
    
    this.year = year;

    return {
      countryCode: 'AT',
      year,
      currency: 'EUR',
      minimumWageHourly: this.austrianConstants.minimum_wage_hourly,
      minimumWageMonthly: this.austrianConstants.minimum_wage_monthly,
      minimumWageAnnual: this.austrianConstants.minimum_wage_monthly * 14, // Austria has 14 salaries
      standardHoursPerWeek: 40,
      maxHoursPerWeek: 50, // Austrian labor law
      maxHoursPerDay: 10,
      taxSystem: 'PROGRESSIVE',
      taxBrackets: [
        { min: 0, max: this.austrianConstants.basic_tax_allowance, rate: 0 },
        { min: this.austrianConstants.basic_tax_allowance, max: this.austrianConstants.tax_rate_20_percent_limit, rate: 0.20 },
        { min: this.austrianConstants.tax_rate_20_percent_limit, max: this.austrianConstants.tax_rate_32_5_percent_limit, rate: 0.325 },
        { min: this.austrianConstants.tax_rate_32_5_percent_limit, max: this.austrianConstants.tax_rate_42_percent_limit, rate: 0.42 },
        { min: this.austrianConstants.tax_rate_42_percent_limit, max: this.austrianConstants.tax_rate_48_percent_limit, rate: 0.48 },
        { min: this.austrianConstants.tax_rate_48_percent_limit, max: this.austrianConstants.tax_rate_50_percent_limit, rate: 0.50 },
        { min: this.austrianConstants.tax_rate_50_percent_limit, max: null, rate: 0.55 }
      ],
      personalAllowance: this.austrianConstants.basic_tax_allowance,
      dependentAllowances: {
        child: 0 // Austria uses Familienbeihilfe (family allowance) system
      },
      socialInsuranceRates: {
        employee: {
          pension: this.austrianConstants.pension_rate_employee,
          unemployment: this.austrianConstants.unemployment_rate_employee,
          health: this.austrianConstants.health_rate_employee
        },
        employer: {
          pension: this.austrianConstants.pension_rate_employer,
          unemployment: this.austrianConstants.unemployment_rate_employer,
          health: this.austrianConstants.health_rate_employer,
          accident: this.austrianConstants.accident_rate_employer,
          family_fund: this.austrianConstants.family_fund_rate_employer,
          chamber_fee: this.austrianConstants.chamber_fee_rate
        }
      },
      healthInsuranceRates: {
        employee: this.austrianConstants.health_rate_employee,
        employer: this.austrianConstants.health_rate_employer
      },
      maxAssessmentBase: this.austrianConstants.pension_assessment_limit_monthly,
      minAssessmentBase: 0,
      countrySpecificConstants: {
        contractTypes: ['VOLLZEIT', 'TEILZEIT', 'GERINGFÜGIG', 'BEFRISTET'],
        geringfuegigThreshold: this.austrianConstants.geringfuegig_threshold,
        specialPaymentTaxLimit: this.austrianConstants.special_payment_tax_limit,
        hasSpecialPayments: true, // 13th and 14th salary
        chamberFeeRate: this.austrianConstants.chamber_fee_rate
      },
      validationRules: {
        mandatoryEmployeeFields: ['sozialversicherungsnummer', 'contract_type'],
        employeeFieldValidators: {
          sozialversicherungsnummer: { type: 'custom', customValidator: 'validateAustrianSVN' },
          contract_type: { type: 'string', pattern: '^(VOLLZEIT|TEILZEIT|GERINGFÜGIG|BEFRISTET)$' }
        },
        contractTypes: ['VOLLZEIT', 'TEILZEIT', 'GERINGFÜGIG', 'BEFRISTET'],
        payFrequencies: ['monthly', 'special_payments']
      }
    };
  }

  /**
   * Calculate gross pay components for Austrian employees
   */
  protected async calculateGrossComponents(
    employee: Employee,
    inputData: PayrollInputData
  ): Promise<GrossPayComponents> {
    this.log(`Calculating gross components for Austrian employee ${employee.name}`);
    
    this.employee = employee;
    
    const hourlyRate = this.getHourlyRate(employee);
    this.log(`Using hourly rate: €${hourlyRate}/hour`);
    
    // Calculate base pay
    const basePay = inputData.regularHours * hourlyRate;
    const overtimePay = inputData.overtimeHours * hourlyRate * (1 + this.austrianConstants.overtime_premium_rate);
    
    // Austrian-specific premiums
    const nightPay = (inputData.specialHours.night || 0) * hourlyRate * this.austrianConstants.night_work_premium_rate;
    const sundayPay = (inputData.specialHours.sunday || 0) * hourlyRate * this.austrianConstants.sunday_premium_rate;
    const holidayPay = (inputData.specialHours.holiday || 0) * hourlyRate * this.austrianConstants.holiday_premium_rate;
    
    // Absence pay
    const vacationPay = inputData.vacationDays * hourlyRate * 8;
    const sickPay = inputData.sickLeaveDays * hourlyRate * 8;
    
    const specialPay = {
      night: nightPay,
      sunday: sundayPay,
      holiday: holidayPay
    };
    
    const absencePay = {
      vacation: vacationPay,
      sickLeave: sickPay
    };
    
    const total = basePay + overtimePay + nightPay + sundayPay + holidayPay + 
                 vacationPay + sickPay + inputData.bonuses + inputData.otherIncome;
    
    this.log(`Austrian gross components: Base €${basePay}, OT €${overtimePay}, Total €${total}`);
    
    return {
      basePay,
      overtimePay,
      specialPay,
      absencePay,
      bonuses: inputData.bonuses,
      otherIncome: inputData.otherIncome,
      total
    };
  }

  /**
   * Calculate Austrian social insurance contributions
   */
  protected async calculateInsuranceContributions(
    employee: Employee,
    grossComponents: GrossPayComponents,
    inputData: PayrollInputData
  ): Promise<Record<string, InsuranceCalculation>> {
    this.log(`Calculating Austrian insurance contributions`);
    
    const contractType = employee.contract_type as AustrianContractType;
    const grossSalary = grossComponents.total;
    
    // Handle Geringfügige Beschäftigung (marginal employment)
    if (contractType === 'GERINGFÜGIG' || grossSalary <= this.austrianConstants.geringfuegig_threshold) {
      return this.calculateGeringfuegigInsurance(grossSalary);
    }
    
    // Standard employee insurance calculation
    return this.calculateStandardAustrianInsurance(grossSalary);
  }

  /**
   * Calculate Austrian income tax
   */
  protected async calculateTax(
    employee: Employee,
    grossComponents: GrossPayComponents,
    insuranceCalculations: Record<string, InsuranceCalculation>,
    inputData: PayrollInputData
  ): Promise<TaxCalculation> {
    this.log(`Calculating Austrian tax`);
    
    const totalInsuranceEmployee = Object.values(insuranceCalculations)
      .reduce((sum, calc) => sum + calc.employeeTotal, 0);
    
    const taxBase = grossComponents.total - totalInsuranceEmployee;
    
    // Apply basic tax allowance
    const taxableIncome = Math.max(0, taxBase - this.austrianConstants.basic_tax_allowance);
    
    // Calculate income tax using Austrian progressive brackets
    const incomeTax = this.calculateAustrianIncomeTax(taxableIncome);
    
    return {
      taxBase,
      taxableIncome,
      allowances: {
        basic: this.austrianConstants.basic_tax_allowance
      },
      taxBrackets: {
        incomeTax
      },
      credits: {},
      totalTax: incomeTax,
      netTaxBenefit: 0
    };
  }

  /**
   * Calculate other Austrian deductions
   */
  protected async calculateOtherDeductions(
    employee: Employee,
    grossComponents: GrossPayComponents,
    inputData: PayrollInputData
  ): Promise<DeductionCalculation[]> {
    this.log(`Calculating Austrian other deductions`);
    
    const deductions: DeductionCalculation[] = [];
    
    // Chamber fee (Kammerumlage)
    const chamberFee = Math.min(
      grossComponents.total * this.austrianConstants.chamber_fee_rate,
      this.austrianConstants.chamber_fee_limit
    );
    
    if (chamberFee > 0) {
      deductions.push({
        type: 'OTHER',
        amount: chamberFee,
        description: 'Chamber Fee (Kammerumlage)',
        mandatory: true
      });
    }
    
    // Advance payments
    if (inputData.advances > 0) {
      deductions.push({
        type: 'OTHER',
        amount: inputData.advances,
        description: 'Advance Payments (Vorschuss)',
        mandatory: true
      });
    }
    
    // Other deductions
    Object.entries(inputData.otherDeductions || {}).forEach(([key, amount]) => {
      if (amount > 0) {
        deductions.push({
          type: 'OTHER',
          amount,
          description: key,
          mandatory: false
        });
      }
    });
    
    return deductions;
  }

  /**
   * Austrian-specific validation
   */
  protected async validateCountrySpecificData(
    employee: Employee,
    inputData: PayrollInputData
  ): Promise<Record<string, string[]>> {
    const errors: Record<string, string[]> = {};
    
    // Validate contract type
    if (!employee.contract_type || !['VOLLZEIT', 'TEILZEIT', 'GERINGFÜGIG', 'BEFRISTET'].includes(employee.contract_type)) {
      errors.contract_type = ['Invalid or missing contract type for Austrian employee'];
    }
    
    // Validate SVN if provided
    if (employee.sozialversicherungsnummer && !this.validateAustrianSVN(employee.sozialversicherungsnummer)) {
      errors.sozialversicherungsnummer = ['Invalid Austrian social insurance number format'];
    }
    
    // Validate Geringfügig limits
    if (employee.contract_type === 'GERINGFÜGIG') {
      const monthlyGross = inputData.regularHours * (this.getHourlyRate(employee) || 0);
      if (monthlyGross > this.austrianConstants.geringfuegig_threshold) {
        errors.contract_type = [`Geringfügig salary cannot exceed €${this.austrianConstants.geringfuegig_threshold} per month`];
      }
    }
    
    return errors;
  }

  /**
   * Helper methods
   */
  
  private createDefaultAustrianConstants(year: number): AustrianPayrollConstants {
    return {
      year,
      country_code: 'AT',
      minimum_wage_hourly: 10.0, // Approximate minimum wage
      minimum_wage_monthly: 1733, // 2024 minimum wage
      basic_tax_allowance: 11000,
      tax_rate_20_percent_limit: 18000,
      tax_rate_32_5_percent_limit: 31000,
      tax_rate_42_percent_limit: 60000,
      tax_rate_48_percent_limit: 90000,
      tax_rate_50_percent_limit: 1000000,
      tax_rate_55_percent: 0.55,
      pension_rate_employee: 0.1025,
      pension_rate_employer: 0.1255,
      unemployment_rate_employee: 0.03,
      unemployment_rate_employer: 0.03,
      accident_rate_employer: 0.013,
      health_rate_employee: 0.0387,
      health_rate_employer: 0.0378,
      family_fund_rate_employer: 0.045,
      pension_assessment_limit_monthly: 6060,
      health_assessment_limit_monthly: 5550,
      geringfuegig_threshold: 500.91,
      special_payment_tax_limit: 620,
      overtime_premium_rate: 0.50,
      night_work_premium_rate: 0.25,
      sunday_premium_rate: 1.00,
      holiday_premium_rate: 1.00,
      chamber_fee_rate: 0.005,
      chamber_fee_limit: 15
    };
  }

  private calculateGeringfuegigInsurance(grossSalary: number): Record<string, InsuranceCalculation> {
    // Marginal employment - no employee contributions, minimal employer contributions
    const employerAccident = grossSalary * 0.013;
    
    return {
      pension: {
        employee: { main: 0 },
        employer: { main: 0 }, // Can be insured voluntarily
        employeeTotal: 0,
        employerTotal: 0
      },
      health: {
        employee: { main: 0 },
        employer: { main: 0 }, // Separate insurance usually
        employeeTotal: 0,
        employerTotal: 0
      },
      unemployment: {
        employee: { main: 0 },
        employer: { main: 0 },
        employeeTotal: 0,
        employerTotal: 0
      },
      accident: {
        employee: { main: 0 },
        employer: { main: employerAccident },
        employeeTotal: 0,
        employerTotal: employerAccident
      }
    };
  }

  private calculateStandardAustrianInsurance(grossSalary: number): Record<string, InsuranceCalculation> {
    const pensionAssessmentBase = Math.min(grossSalary, this.austrianConstants.pension_assessment_limit_monthly);
    const healthAssessmentBase = Math.min(grossSalary, this.austrianConstants.health_assessment_limit_monthly);
    
    return {
      pension: {
        employee: { main: pensionAssessmentBase * this.austrianConstants.pension_rate_employee },
        employer: { main: pensionAssessmentBase * this.austrianConstants.pension_rate_employer },
        employeeTotal: pensionAssessmentBase * this.austrianConstants.pension_rate_employee,
        employerTotal: pensionAssessmentBase * this.austrianConstants.pension_rate_employer
      },
      health: {
        employee: { main: healthAssessmentBase * this.austrianConstants.health_rate_employee },
        employer: { main: healthAssessmentBase * this.austrianConstants.health_rate_employer },
        employeeTotal: healthAssessmentBase * this.austrianConstants.health_rate_employee,
        employerTotal: healthAssessmentBase * this.austrianConstants.health_rate_employer
      },
      unemployment: {
        employee: { main: pensionAssessmentBase * this.austrianConstants.unemployment_rate_employee },
        employer: { main: pensionAssessmentBase * this.austrianConstants.unemployment_rate_employer },
        employeeTotal: pensionAssessmentBase * this.austrianConstants.unemployment_rate_employee,
        employerTotal: pensionAssessmentBase * this.austrianConstants.unemployment_rate_employer
      },
      accident: {
        employee: { main: 0 }, // Employer-only
        employer: { main: grossSalary * this.austrianConstants.accident_rate_employer },
        employeeTotal: 0,
        employerTotal: grossSalary * this.austrianConstants.accident_rate_employer
      },
      family_fund: {
        employee: { main: 0 }, // Employer-only
        employer: { main: grossSalary * this.austrianConstants.family_fund_rate_employer },
        employeeTotal: 0,
        employerTotal: grossSalary * this.austrianConstants.family_fund_rate_employer
      }
    };
  }

  private calculateAustrianIncomeTax(taxableIncome: number): number {
    let tax = 0;
    let remainingIncome = taxableIncome;
    
    // Tax bracket calculation
    const brackets = [
      { limit: this.austrianConstants.tax_rate_20_percent_limit - this.austrianConstants.basic_tax_allowance, rate: 0.20 },
      { limit: this.austrianConstants.tax_rate_32_5_percent_limit - this.austrianConstants.tax_rate_20_percent_limit, rate: 0.325 },
      { limit: this.austrianConstants.tax_rate_42_percent_limit - this.austrianConstants.tax_rate_32_5_percent_limit, rate: 0.42 },
      { limit: this.austrianConstants.tax_rate_48_percent_limit - this.austrianConstants.tax_rate_42_percent_limit, rate: 0.48 },
      { limit: this.austrianConstants.tax_rate_50_percent_limit - this.austrianConstants.tax_rate_48_percent_limit, rate: 0.50 }
    ];
    
    for (const bracket of brackets) {
      if (remainingIncome <= 0) break;
      
      const taxableAtThisRate = Math.min(remainingIncome, bracket.limit);
      tax += taxableAtThisRate * bracket.rate;
      remainingIncome -= taxableAtThisRate;
    }
    
    // Top bracket (55%)
    if (remainingIncome > 0) {
      tax += remainingIncome * 0.55;
    }
    
    return Math.round(tax * 100) / 100;
  }

  private validateAustrianSVN(svn: string): boolean {
    // Basic validation for Austrian social insurance number (10 digits)
    const pattern = /^\d{10}$/;
    return pattern.test(svn.replace(/\s/g, ''));
  }
}